import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { Mail, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useListMailAccounts } from "@workspace/api-client-react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useTranslation } from "react-i18next";
import { useUser } from "@clerk/react";

const DISMISS_KEY = "onenext.mail_connect_dismissed";

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "true";
  } catch {
    return false;
  }
}

function setDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, "true");
  } catch {}
}

function readSkippedMail(userId: string | undefined): boolean {
  try {
    const key = userId
      ? `onenext_onboarding_skipped_mail:${userId}`
      : "onenext_onboarding_skipped_mail";
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

export default function MailConnectBanner() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [dismissed, setDismissedState] = useState(isDismissed);
  const { active: isDemoActive } = useDemoMode();
  const { user, isLoaded } = useUser();

  const mailAccounts = useListMailAccounts();

  const skippedMail = useMemo(() => {
    if (!isLoaded) return false;
    return readSkippedMail(user?.id);
  }, [isLoaded, user?.id]);

  if (isDemoActive) return null;
  if (dismissed) return null;
  if (!mailAccounts.isSuccess) return null;
  if ((mailAccounts.data?.length ?? 0) > 0) return null;

  const handleDismiss = () => {
    setDismissed();
    setDismissedState(true);
  };

  const handleConnect = () => {
    setLocation("/app/settings");
  };

  const descKey = skippedMail
    ? "mail_connect_banner.desc_skipped"
    : "mail_connect_banner.desc";

  return (
    <div
      data-testid="mail-connect-banner"
      className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-primary/20 bg-primary/5 px-4 py-2.5 text-sm shadow-sm"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Mail className="w-4 h-4 shrink-0 text-primary" />
        <span className="text-foreground truncate">
          <span className="font-semibold">{t("mail_connect_banner.title")}</span>
          <span className="hidden sm:inline text-muted-foreground">
            {" — "}
            {t(descKey)}
          </span>
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="default"
          onClick={handleConnect}
          className="gap-1.5 h-7 text-xs px-3"
          data-testid="mail-connect-banner-cta"
        >
          {t("mail_connect_banner.cta")}
          <ArrowRight className="w-3 h-3" />
        </Button>
        <button
          onClick={handleDismiss}
          aria-label={t("mail_connect_banner.dismiss")}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
          data-testid="mail-connect-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
